import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema, PaginatedResponseSchema } from './common';

/**
 * Programa académico (ver docs/00-compartido/esquemas-datos.md §1).
 * Tabla `programa` (admin): nombre, area, descripcion, y columnas legacy
 * (`duracion_dias`, `num_sesiones`) que se conservan por compat pero ya no
 * se exigen en el formulario de creación (la duración sale de la cohorte
 * y `num_sesiones` se calcula por `count(sesion)` cuando corresponda).
 *
 * Refactor v2: se añade `descripcion` (texto libre del catálogo) y se
 * mantienen los legacy fields como opcionales. La limpieza/eliminación
 * definitiva se hará en una fase posterior.
 */
export const ProgramaSchema = z.object({
  id: EntityIdSchema,
  nombre: z.string(),
  area: z.string(),
  descripcion: z.string().nullable().optional(),
  // Legacy columns (mantener en el schema para no romper `parse()` hasta que
  // el frontend se actualice; el nuevo `ProgramaCreateInput` ya no las pide).
  duracion_dias: z.number().int().positive().nullable().optional(),
  num_sesiones: z.number().int().positive().nullable().optional(),
  carga_horaria_global: z.number().int().nonnegative().nullable().optional(),
  creditos_globales: z.number().int().nonnegative().nullable().optional(),
  created_at: IsoDateTimeSchema,
});
export type Programa = z.infer<typeof ProgramaSchema>;

/**
 * Input para POST /api/v1/programas.
 *
 * Refactor v2: solo `nombre` y `area` son obligatorios. Las columnas legacy
 * (`duracion_dias`, `num_sesiones`) ya no se piden: la duración del programa
 * la define la cohorte, y el número de sesiones es dinámico.
 */
export const ProgramaCreateInputSchema = z.object({
  nombre: z.string().min(3).max(120),
  area: z.string().default('Formación Permanente y Docencia'),
  descripcion: z.string().max(2000).optional(),
  carga_horaria_global: z.number().int().nonnegative().optional(),
  creditos_globales: z.number().int().nonnegative().optional(),
});
export type ProgramaCreateInput = z.infer<typeof ProgramaCreateInputSchema>;

/**
 * Input para PATCH /api/v1/programas/:id (todos opcionales).
 */
export const ProgramaUpdateInputSchema = ProgramaCreateInputSchema.partial();
export type ProgramaUpdateInput = z.infer<typeof ProgramaUpdateInputSchema>;

/**
 * Response de GET /api/v1/programas (paginado).
 */
export const ProgramaListResponseSchema = PaginatedResponseSchema(ProgramaSchema);
export type ProgramaListResponse = z.infer<typeof ProgramaListResponseSchema>;